import { useState, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"

export interface ClientOption {
  id:       string
  name:     string
  code:     string
  sgtCodes: string[] | null
}

interface UseClientsSearchOptions {
  /** Only fetch when this is true (e.g. when a sheet is open). Default: true */
  enabled?: boolean
  /** Filter only clients that have at least one SGT code. Default: false */
  onlyWithSgt?: boolean
}

/**
 * Search clients server-side with debounce.
 * Fetches up to 50 results matching the search term — avoids loading all 800+ clients at once.
 */
export function useClientsSearch({ enabled = true, onlyWithSgt = false }: UseClientsSearchOptions = {}) {
  const [inputValue, setInputValue]   = useState("")
  const [search, setSearch]           = useState("")
  const debounceRef                   = useRef<ReturnType<typeof setTimeout> | null>(null)

  function onSearchChange(value: string) {
    setInputValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setSearch(value), 300)
  }

  const { data: clients = [], isLoading } = useQuery<ClientOption[]>({
    queryKey: ["clients-search", search, onlyWithSgt],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const params = new URLSearchParams({
        deleted:  "false",
        pageSize: "50",
      })
      if (search.trim()) params.set("search", search.trim())
      if (onlyWithSgt)   params.set("sgt", "with")
      const res  = await authorizedFetch(`${API_URL}/api/clients?${params}`)
      if (!res.ok) return []
      const json = await res.json() as { data: ClientOption[] }
      return json.data
    },
  })

  return { clients, isLoading, inputValue, onSearchChange, search }
}
